import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PageHeader, Card, Button, Badge, LoadingState, Alert } from '../components/ui';
import groupService from '../services/groupService';
import {
  ArrowLeftIcon,
  TrashIcon,
  PencilIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const GroupDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingGroupInfo, setIsLoadingGroupInfo] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    theme: '',
    language: '',
    country: '',
    region: '',
    city: '',
    member_count: 0,
    quality: 50
  });

  // Fetch group details
  const {
    data: group,
    isLoading,
    error: groupError
  } = useQuery(
    ['group', id],
    () => groupService.getGroup(id),
    {
      retry: 1,
      onSuccess: (data) => {
        // Initialize form data with group data
        setFormData({
          title: data.title || '',
          theme: data.theme || '',
          language: data.language || '',
          country: data.country || '',
          region: data.region || '',
          city: data.city || '',
          member_count: data.member_count || 0,
          quality: data.quality || 50
        });
      },
      onError: (err) => {
        console.error('Error fetching group:', err);
      }
    }
  );

  // Update group mutation
  const updateGroupMutation = useMutation(
    (groupData) => groupService.updateGroup(id, groupData),
    {
      onSuccess: () => {
        setSuccess('Group updated successfully');
        setIsEditing(false);
        queryClient.invalidateQueries(['group', id]);
      },
      onError: (err) => {
        setError(`Failed to update group: ${err.message}`);
      }
    }
  );

  // Delete group mutation
  const deleteGroupMutation = useMutation(
    () => groupService.deleteGroup(id),
    {
      onSuccess: () => {
        setSuccess('Group deleted successfully');
        // Redirect to groups list after a short delay
        setTimeout(() => {
          navigate('/groups');
        }, 2000);
      },
      onError: (err) => {
        setError(`Failed to delete group: ${err.message}`);
      }
    }
  );

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    updateGroupMutation.mutate(formData);
  };

  // Handle delete group
  const handleDeleteGroup = () => {
    if (window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      deleteGroupMutation.mutate();
    }
  };
  
  // Handle get group info
  const handleGetGroupInfo = async () => {
    try {
      setIsLoadingGroupInfo(true);
      setError('');
      
      // Call the API to get group info
      const result = await groupService.getGroupInfo(id);
      
      // Update the group data
      queryClient.invalidateQueries(['group', id]);
      
      setSuccess('Group information updated successfully');
    } catch (err) {
      setError(`Failed to get group info: ${err.message}`);
    } finally {
      setIsLoadingGroupInfo(false);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingState message="Loading group details..." />;
  }

  // Error state
  if (groupError) {
    return (
      <div className="text-center">
        <Alert variant="error" className="mb-4">
          {groupError.message || 'Failed to load group details'}
        </Alert>
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/groups')}
        >
          Back to Groups
        </Button>
      </div>
    );
  }

  // Not found state
  if (!group) {
    return (
      <div className="text-center">
        <Alert variant="error" className="mb-4">
          Group not found
        </Alert>
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/groups')}
        >
          Back to Groups
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Group Details"
        description={`Manage group ${group.title || 'Unknown'}`}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeftIcon}
            onClick={() => navigate('/groups')}
          >
            Back
          </Button>
        }
      />

      {error && (
        <Alert
          variant="error"
          className="mb-4"
          isDismissible
          onDismiss={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          variant="success"
          className="mb-4"
          isDismissible
          onDismiss={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card 
          title="Group Information"
          actions={
            isEditing ? (
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={XMarkIcon}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={CheckIcon}
                  onClick={handleSubmit}
                  isLoading={updateGroupMutation.isLoading}
                  disabled={updateGroupMutation.isLoading}
                >
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                icon={PencilIcon}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )
          }
        >
          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="member_count" className="block text-sm font-medium text-gray-700">Member Count</label>
                <input
                  type="number"
                  id="member_count"
                  name="member_count"
                  value={formData.member_count}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="theme" className="block text-sm font-medium text-gray-700">Theme</label>
                <input
                  type="text"
                  id="theme"
                  name="theme"
                  value={formData.theme}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700">Language</label>
                <input
                  type="text"
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700">Region</label>
                <input
                  type="text"
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="quality" className="block text-sm font-medium text-gray-700">Quality (0-100)</label>
                <input
                  type="number"
                  id="quality"
                  name="quality"
                  min="0"
                  max="100"
                  value={formData.quality}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Title</h4>
                <p className="mt-1 text-sm text-gray-900">{group.title || 'Not available'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Link</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {group.chat_link ? (
                    <a 
                      href={group.chat_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-900"
                    >
                      {group.chat_link}
                    </a>
                  ) : 'Not available'}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Username</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {group.chat_username ? `@${group.chat_username}` : 'Not available'}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Member Count</h4>
                <p className="mt-1 text-sm text-gray-900">{group.member_count || 0}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Theme</h4>
                <p className="mt-1 text-sm text-gray-900">{group.theme || 'Not specified'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Language</h4>
                <p className="mt-1 text-sm text-gray-900">{group.language || 'Not specified'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Country</h4>
                <p className="mt-1 text-sm text-gray-900">{group.country || 'Not specified'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Region</h4>
                <p className="mt-1 text-sm text-gray-900">{group.region || 'Not specified'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">City</h4>
                <p className="mt-1 text-sm text-gray-900">{group.city || 'Not specified'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Quality</h4>
                <div className="mt-1">
                  {group.quality >= 70 ? (
                    <Badge variant="green">High ({group.quality})</Badge>
                  ) : group.quality >= 40 ? (
                    <Badge variant="yellow">Medium ({group.quality})</Badge>
                  ) : (
                    <Badge variant="red">Low ({group.quality})</Badge>
                  )}
                </div>
              </div>
              
              {group.created_at && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Created</h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(group.created_at).toLocaleString()}
                  </p>
                </div>
              )}
              
              {group.updated_at && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Last Updated</h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(group.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card title="Associated Accounts">
            <div className="prose max-w-none mb-4">
              <p>
                Accounts that have joined this group.
              </p>
            </div>

            {group.accounts && group.accounts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {group.accounts.map((account) => (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {account.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {account.phonenumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {account.status === 'on' ? (
                            <Badge variant="green">Active</Badge>
                          ) : account.status === 'off' ? (
                            <Badge variant="gray">Inactive</Badge>
                          ) : account.status === 'banned' ? (
                            <Badge variant="red">Banned</Badge>
                          ) : (
                            <Badge variant="gray">{account.status || 'Unknown'}</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="secondary"
                            size="sm"
                            to={`/accounts/${account.id}`}
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">
                No accounts have joined this group yet.
              </p>
            )}
          </Card>

          <Card title="Group Actions">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">
                  Get updated information about this group, including member count and forum status.
                </p>
                <Button
                  variant="secondary"
                  icon={InformationCircleIcon}
                  onClick={handleGetGroupInfo}
                  isLoading={isLoadingGroupInfo}
                  disabled={isLoadingGroupInfo}
                >
                  Get Group Info
                </Button>
              </div>
            </div>
          </Card>
          
          <Card title="Danger Zone">
            <p className="text-sm text-gray-500 mb-4">
              Permanently delete this group from the system.
            </p>
            <Button
              variant="danger"
              icon={TrashIcon}
              onClick={handleDeleteGroup}
              isLoading={deleteGroupMutation.isLoading}
              disabled={deleteGroupMutation.isLoading}
            >
              Delete Group
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GroupDetail;
